
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface VariantOptionFormValues {
  option_name: string;
  option_value: string;
  is_default: boolean;
  default_selected_option: boolean;
  variant: string[];
  factories: string[];
  price_change: number;
  image_url?: string;
  variant_group?: Record<string, number>;
}

export function useVariantOptions() {
  const [variantOptions, setVariantOptions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchVariantOptions = async () => {
    try {
      setLoading(true);
      
      // First fetch variant options
      const { data: variantOptionsData, error: variantOptionsError } = await supabase
        .from("variant_options")
        .select("*")
        .order("option_name");

      if (variantOptionsError) throw variantOptionsError;

      // Then fetch variants to map IDs to names for display
      const { data: variantsData, error: variantsError } = await supabase
        .from("variants")
        .select("id, name");

      if (variantsError) throw variantsError;

      // Create a mapping of variant IDs to names
      const variantIdToName = variantsData?.reduce((acc: any, variant: any) => {
        acc[variant.id] = variant.name;
        return acc;
      }, {}) || {};

      // Map variant IDs to names in the variant options data for display
      const enhancedData = variantOptionsData?.map(option => ({
        ...option,
        variant_names: Array.isArray(option.variant) ? 
          option.variant.map((variantId: string) => variantIdToName[variantId] || variantId) : 
          []
      })) || [];

      setVariantOptions(enhancedData);
    } catch (error: any) {
      toast({
        title: "Error fetching variant options",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const createVariantOption = async (values: VariantOptionFormValues) => {
    try {
      const { error } = await supabase
        .from("variant_options")
        .insert({
          option_name: values.option_name,
          option_value: values.option_value,
          is_default: values.is_default,
          default_selected_option: values.default_selected_option,
          variant: values.variant, // Store variant IDs
          factories: values.factories,
          price_change: values.price_change || 0,
          image_url: values.image_url || null,
          variant_group: values.variant_group || {},
        });

      if (error) throw error;
      toast({
        title: "Variant Option Created",
        description: `${values.option_name} has been created.`
      });
      
      await fetchVariantOptions();
      return true;
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
      return false;
    }
  };

  const updateVariantOption = async (id: string, values: VariantOptionFormValues) => {
    try {
      const { error } = await supabase
        .from("variant_options")
        .update({
          option_name: values.option_name,
          option_value: values.option_value,
          is_default: values.is_default,
          default_selected_option: values.default_selected_option,
          variant: values.variant, // Store variant IDs
          factories: values.factories,
          price_change: values.price_change || 0,
          image_url: values.image_url || null,
          variant_group: values.variant_group || {},
          updated_at: new Date().toISOString()
        })
        .eq("id", id);

      if (error) throw error;
      toast({
        title: "Variant Option Updated",
        description: `${values.option_name} has been updated.`
      });
      
      await fetchVariantOptions();
      return true;
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
      return false;
    }
  };

  const deleteVariantOption = async (variantOption: any) => {
    try {
      const { error } = await supabase
        .from("variant_options")
        .delete()
        .eq("id", variantOption.id);

      if (error) throw error;
      
      toast({
        title: "Variant Option Deleted",
        description: `${variantOption.option_name} has been deleted.`
      });
      
      await fetchVariantOptions();
      return true;
    } catch (error: any) {
      toast({
        title: "Error deleting variant option",
        description: error.message,
        variant: "destructive",
      });
      return false;
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchVariantOptions();
  }, []);

  return {
    variantOptions,
    loading,
    fetchVariantOptions,
    createVariantOption,
    updateVariantOption,
    deleteVariantOption
  };
}
